import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TournamentCard from '@/components/TournamentCard';
import type { TournamentCardFavorite } from '@/components/TournamentCard';
import { TournamentSeasonRoleFilters } from '@/components/TournamentSeasonRoleFilters';
import { TournamentStatusFilter } from '@/components/TournamentStatusFilter';
import { TournamentsFilterBar } from '@/components/TournamentsFilterBar';
import { MainSection } from '@/components/layout/MainSection';
import { ListPagination } from '@/components/ui/ListPagination';
import { useTournamentListFilters } from '@/hooks/useTournamentListFilters';
import { useHistoryScrollPosition } from '@/hooks/useHistoryScrollPosition';
import { useTournamentFavorite } from '@/hooks/useTournamentFavorite';
import { useTournamentPagedList } from '@/hooks/useTournamentList';
import { TOURNAMENT_PAGE_SIZES } from '@/utils/tournamentListParams';
import type { Tournament, TournamentListSource } from '@/types';
import styles from './styles.module.scss';

export interface TournamentsListViewProps {
  source: TournamentListSource;
  /** The tab is on screen (mobile keeps inactive tab slides mounted). */
  isActive: boolean;
  title: string;
  /** Replaces the item count in the header (e.g. the mobile "Create" button). */
  headerAction?: React.ReactNode;
  /** Shown when the user has no tournaments at all in this list. */
  emptyState: React.ReactNode;
  /**
   * Card per row; defaults to a plain `TournamentCard`. `favorite` is the
   * favorite toggle of that row, absent where the row has none.
   */
  renderCard?: (tournament: Tournament, favorite?: TournamentCardFavorite) => React.ReactNode;
  /** Inline message above the list (e.g. an action error). */
  message?: React.ReactNode;
  /**
   * Filters under the search: `status` segments (active lists) or `seasonRole`
   * year + role dropdowns (archive, where every tournament is closed).
   */
  filterSet?: 'status' | 'seasonRole';
}

interface TournamentsListEmptyProps {
  title: string;
  text: string;
  actions?: React.ReactNode;
  testId?: string;
}

/** Empty-state card of a tournament list (title, hint and optional actions). */
export const TournamentsListEmpty: React.FC<TournamentsListEmptyProps> = ({ title, text, actions, testId }) => (
  <div className={styles.emptyState} data-testid={testId}>
    <h2 className={styles.emptyStateTitle}>{title}</h2>
    <p className={styles.emptyStateText}>{text}</p>
    {actions && <div className={styles.emptyStateActions}>{actions}</div>}
  </div>
);

const defaultRenderCard = (tournament: Tournament, favorite?: TournamentCardFavorite) => (
  <TournamentCard key={tournament.id} tournament={tournament} favorite={favorite} />
);

/**
 * Tournament list tab body inside a `MainSection`: one scroll container with
 * the filter bar (scrolls away with the list, the section header stays), one
 * page of cards ending with the pager, and empty / error states.
 */
export const TournamentsListView: React.FC<TournamentsListViewProps> = ({
  source,
  isActive,
  title,
  headerAction,
  emptyState,
  renderCard = defaultRenderCard,
  message,
  filterSet = 'status',
}) => {
  const { t } = useTranslation('tournament');
  const filters = useTournamentListFilters(isActive);
  const isStatusSet = filterSet === 'status';
  const status = isStatusSet ? filters.status : null;
  const season = isStatusSet ? null : filters.season;
  const role = isStatusSet ? null : filters.role;
  const scrollRef = useRef<HTMLDivElement>(null);
  const favoriteFor = useTournamentFavorite(source);
  const { items, meta, error, isPending } = useTournamentPagedList(source, {
    page: filters.page + 1,
    limit: filters.pageSize,
    search: filters.search || undefined,
    status: status ?? undefined,
    season: season ?? undefined,
    role: role ?? undefined,
  });

  // Back from a tournament returns to the same scroll offset (page and
  // filters come back through the URL).
  useHistoryScrollPosition(scrollRef, `tournamentsListScroll:${source}`, items.length > 0);

  const hasFilters = filters.search !== '' || status !== null || season !== null || role !== null;
  const allCount = meta
    ? Object.values(meta.statusCounts).reduce((sum, n) => sum + n, 0)
    : 0;
  const isTrulyEmpty = meta !== undefined && !hasFilters && allCount === 0;

  // A page past the end (e.g. the last item of the last page was archived)
  // falls back to the last existing page.
  const { setPage } = filters;
  useEffect(() => {
    if (meta && meta.total > 0 && filters.page >= meta.totalPages) {
      setPage(meta.totalPages - 1);
    }
  }, [meta, filters.page, setPage]);

  // New filters or page start the list from the top. Compared with the
  // previous value (not "first render") so a remount — back navigation,
  // StrictMode's effect replay — keeps the restored scroll position.
  const viewKey = [filters.search, status, season, role, filters.page, filters.pageSize].join('|');
  const prevViewKey = useRef(viewKey);
  useEffect(() => {
    if (prevViewKey.current === viewKey) return;
    prevViewKey.current = viewKey;
    scrollRef.current?.scrollTo({ top: 0 });
  }, [viewKey]);

  const filteredEmpty = (
    <TournamentsListEmpty
      title={t('tournament:list.noResultsTitle')}
      text={t('tournament:list.noResultsText')}
      testId={`tournaments-${source}-no-results`}
      actions={(
        <button
          type="button"
          className="ls-btn ls-btn--outlined"
          onClick={filters.resetFilters}
          data-testid={`tournaments-${source}-reset-filters`}
        >
          {t('tournament:list.resetFilters')}
        </button>
      )}
    />
  );

  let body: React.ReactNode;
  if (isPending && !error) {
    // Not fetched yet (incl. waiting for the user id) — nothing, not the empty state.
    body = null;
  } else if (error && !meta) {
    body = (
      <div className={styles.error}>
        <p className={styles.errorText}>{t('tournament:errorLoadingTournaments')}</p>
      </div>
    );
  } else if (!meta || meta.total === 0) {
    body = hasFilters ? filteredEmpty : emptyState;
  } else {
    body = (
      <div className={styles.cardsGrid}>
        {items.map((tournament) => renderCard(tournament, favoriteFor(tournament)))}
      </div>
    );
  }

  const headerRight = headerAction ?? (meta && (
    <span className={styles.count}>{t('tournament:items', { count: meta.total })}</span>
  ));

  return (
    <MainSection title={title} headerRight={headerRight} hideTitleOnMobile fill>
      <div ref={scrollRef} className={styles.scroll} data-testid={`tournaments-${source}-list`}>
        {meta && !isTrulyEmpty && (
          <TournamentsFilterBar
            query={filters.query}
            onQueryChange={filters.setQuery}
            idPrefix={`tournaments-${source}`}
          >
            {isStatusSet ? (
              <TournamentStatusFilter
                status={filters.status}
                onStatusChange={filters.setStatus}
                statusCounts={meta.statusCounts}
                idPrefix={`tournaments-${source}`}
              />
            ) : (
              <TournamentSeasonRoleFilters
                season={filters.season}
                onSeasonChange={filters.setSeason}
                role={filters.role}
                onRoleChange={filters.setRole}
                idPrefix={`tournaments-${source}`}
              />
            )}
          </TournamentsFilterBar>
        )}
        <div className={styles.content}>
          {message}
          {body}
          {meta && (
            <ListPagination
              page={filters.page}
              pageSize={filters.pageSize}
              total={meta.total}
              pageSizeOptions={TOURNAMENT_PAGE_SIZES}
              onPageChange={filters.setPage}
              onPageSizeChange={filters.setPageSize}
              testIdPrefix={`tournaments-${source}`}
              className={styles.pagination}
            />
          )}
        </div>
      </div>
    </MainSection>
  );
};

export default TournamentsListView;
